'use client'

import { Button, Divider, Spacer } from '@vinted/web-ui'
import React, { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  CountryModel,
  getCountries,
  transformCountryDtos,
} from '@marketplace-web/country/country-data'
import { BusinessRegistrationModel } from '@marketplace-web/business-accounts/business-accounts-data'
import { Heading } from '@marketplace-web/business-accounts/business-accounts-ui'

import {
  BusinessAccountEntityOptions,
  NewBusinessAddressField,
  NewBusinessDetailsField,
} from '../../../../../constants'
import { GeneralError } from '../../../GeneralError'
import { OnValidateRegistrationParams } from '../../../hooks/useUpdateRegistration'
import { Section } from '../../../Section'
import { camelCase } from '../../utils'
import { BusinessAddressInitialDetails } from '../components/BusinessAddressInitialDetails'
import { InputDropdownController, InputDropdownOption } from '../components/InputDropdownController'
import { LegalEntitySection } from '../components/LegalEntitySection'

const TRANSLATION_PREFIX = 'business.registration'

type Props = {
  defaultValues: BusinessRegistrationModel
  showFullForm: boolean
  onFullFormShow: (show: boolean) => void
  apiErrors: Array<{ field: string; value: string }> | null
  showGeneralError: boolean
  onClearDefaultValues: () => void
  onUpdateRegistration: ({
    registrationFields,
    validate,
  }: OnValidateRegistrationParams) => Promise<void>
}

const InitialDetailsStep = ({
  defaultValues,
  apiErrors,
  showGeneralError,
  showFullForm,
  onFullFormShow,
  onClearDefaultValues,
  onUpdateRegistration,
}: Props) => {
  const isPrivateBetaFSEnabled = useFeatureSwitch('allow_eu_countries_pro_registration')
  const { handleSubmit, getValues, setError, clearErrors, reset, watch } =
    useFormContext<Partial<BusinessRegistrationModel>>()
  const [selectedCountry, setSelectedCountry] = useState<CountryModel>()

  const translateAddresses = useTranslate(`${TRANSLATION_PREFIX}.addresses`)
  const translateBusinessDetails = useTranslate(`${TRANSLATION_PREFIX}.business_details`)
  const translateRegistrationAction = useTranslate(`${TRANSLATION_PREFIX}.action`)
  const translateWarningModal = useTranslate(`${TRANSLATION_PREFIX}.warning_modal`)

  const [countryOptions, setCountryOptions] = useState<Array<InputDropdownOption>>([])

  const { fetch: fetchCountries, transformedData: countries } = useFetch(getCountries, response =>
    transformCountryDtos(response.countries),
  )

  const countryInput = watch(NewBusinessAddressField.Country)
  const entityTypeInput = watch(NewBusinessDetailsField.EntityType)

  useEffect(() => {
    if (countryInput) {
      setSelectedCountry(countries?.find(country => country.id.toString() === countryInput))
    }
  }, [countries, countryInput])

  useEffect(() => {
    fetchCountries()
  }, [fetchCountries])

  useEffect(() => {
    const transformedCountries =
      countries?.map(country => ({
        value: country.id.toString(),
        label: country.titleLocal,
      })) || []
    setCountryOptions(transformedCountries)
  }, [countries])

  useEffect(() => {
    if (!apiErrors) return

    apiErrors.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (
        NewBusinessAddressField.Country === fieldName ||
        NewBusinessDetailsField.EntityType === fieldName
      ) {
        setError(fieldName, { type: 'manual', message: value })
      }
    })
  }, [apiErrors, setError])

  const entityOptions = BusinessAccountEntityOptions.map(entity => ({
    value: entity,
    label: translateBusinessDetails(`entity_type.${entity}`),
  }))

  const handleContinue = useCallback(async () => {
    const registrationFields = getValues()
    await onUpdateRegistration({
      registrationFields,
      validate: false,
    })
    onFullFormShow(true)
  }, [getValues, onFullFormShow, onUpdateRegistration])

  useEffect(() => {
    if (countryInput && entityTypeInput && !showFullForm) {
      handleContinue()
    }
  }, [countryInput, entityTypeInput, handleContinue, showFullForm])

  const handleChangeCountry = () => {
    clearErrors()
    reset({
      businessDetails: undefined,
      addresses: undefined,
    })
    onClearDefaultValues()
    onFullFormShow(false)
  }

  const handleChangeEntityType = () => {
    clearErrors()
    reset({
      businessDetails: undefined,
      addresses: undefined,
    })
    onClearDefaultValues()
    onFullFormShow(false)
  }

  useEffect(() => {
    if (!defaultValues.businessDetails || !defaultValues.addresses) return
    reset({ businessDetails: defaultValues.businessDetails, addresses: defaultValues.addresses })
  }, [reset, defaultValues])

  return (
    <SeparatedList separator={<Spacer size="x2-large" />}>
      <Heading
        styling="tight"
        title={translateBusinessDetails('details_step.title')}
        subtitle={translateBusinessDetails('details_step.subtitle')}
      />
      {showGeneralError && <GeneralError />}
      <Section>
        <SeparatedList separator={<Divider />}>
          <InputDropdownController
            title={translateAddresses('business_address.country_id.title')}
            options={countryOptions}
            input={NewBusinessAddressField.Country}
            translationPrefix={TRANSLATION_PREFIX}
            onChange={handleChangeCountry}
            required
            disabled={!isPrivateBetaFSEnabled}
            withWarningModal={showFullForm}
            warningModalTitle={translateWarningModal('title.business_address.country_id')}
            warningModalBody={translateWarningModal('body.business_address.country_id')}
            warningModalContinueText={translateWarningModal(
              'actions.continue.business_address.country_id',
            )}
            warningModalCancelText={translateWarningModal('actions.cancel')}
          />
          <InputDropdownController
            title={translateBusinessDetails('entity_type.title')}
            options={entityOptions}
            input={NewBusinessDetailsField.EntityType}
            translationPrefix={TRANSLATION_PREFIX}
            onChange={handleChangeEntityType}
            required
            disabled={!isPrivateBetaFSEnabled}
            withWarningModal={showFullForm}
            warningModalTitle={translateWarningModal('title.entity_type')}
            warningModalBody={translateWarningModal('body.entity_type')}
            warningModalContinueText={translateWarningModal('actions.continue.entity_type')}
            warningModalCancelText={translateWarningModal('actions.cancel')}
          />
          {showFullForm && (
            <LegalEntitySection
              selectedCountry={selectedCountry}
              defaultValues={defaultValues}
              apiErrors={apiErrors}
            />
          )}
        </SeparatedList>
      </Section>
      {showFullForm && (
        <BusinessAddressInitialDetails
          apiErrors={apiErrors}
          defaultValues={defaultValues}
          selectedCountry={selectedCountry}
          countryOptions={countryOptions}
          showGeneralError={showGeneralError}
        />
      )}
      {!showFullForm && (
        <div className="u-flexbox u-justify-content-flex-end">
          <Button
            size="medium"
            styling="filled"
            text={translateRegistrationAction('continue')}
            onClick={handleSubmit(handleContinue)}
            testId="business-accounts--continue-button"
            inline
          />
        </div>
      )}
    </SeparatedList>
  )
}

export default InitialDetailsStep
